import { useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import { ApiError, errorMessage, get, put } from '../../api.js';
import { useToast } from '../../components/Toast.js';
import { Card, Field, Notice, PageHead, formatDateTime } from '../../components/ui.js';
import { useOffline } from '../../offline/context.js';
import { refreshKioskPin } from '../../offline/kioskPin.js';
import { useSignedIn } from '../../session.js';

interface Status {
  digits: number;
  set: boolean;
  setAt: string | null;
  setBy: string | null;
}

/**
 * Settings > Kiosk PIN (admins, admin.kiosk.pin; brief section 9): the PIN
 * that lets a seller leave Hand the tablet and the booth kiosk. The portal
 * keeps only its hash; a device with network gets it within a minute (and
 * at once when the PIN is typed), so set it before the show.
 */
export function KioskPin() {
  const { refresh } = useSignedIn();
  const { store } = useOffline();
  const toast = useToast();
  const [status, setStatus] = useState<Status | null>(null);
  const [pin, setPin] = useState('');
  const [again, setAgain] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [reload, setReload] = useState(0);

  useEffect(() => {
    let current = true;
    get<Status>('/admin/kiosk-pin')
      .then((s) => current && setStatus(s))
      .catch((err: unknown) => {
        if (err instanceof ApiError && err.status === 401) void refresh();
        else if (current) setError(errorMessage(err));
      });
    return () => {
      current = false;
    };
  }, [refresh, reload]);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!status || saving) return;
    if (!new RegExp(`^\\d{${status.digits}}$`).test(pin)) {
      setError(`The PIN is ${status.digits} digits.`);
      return;
    }
    if (pin !== again) {
      setError('The two PINs are not the same.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await put('/admin/kiosk-pin', { pin });
      setPin('');
      setAgain('');
      // This device has it at once; the others within a minute of their next sync.
      if (store) await refreshKioskPin(store);
      toast('Kiosk PIN set. Devices with network get it within a minute.');
      setReload((n) => n + 1);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <Link to="/settings" className="back">
        Settings
      </Link>
      <PageHead
        title="Kiosk PIN"
        lede="Asked to leave Hand the tablet and the booth kiosk. Share it with the sellers only."
      />
      {status && (
        <Notice kind={status.set ? 'ok' : 'warn'}>
          {status.set
            ? `A PIN is set (${status.setBy ?? 'a former user'}, ${formatDateTime(status.setAt)}). Setting a new one replaces it on every device with network within a minute.`
            : 'No PIN yet: sellers cannot hand the tablet to visitors until one is set.'}
        </Notice>
      )}
      {status && (
        <Card title={status.set ? 'New PIN' : 'Set the PIN'}>
          <form className="vstack" noValidate onSubmit={(e) => void submit(e)}>
            <Field
              label={`PIN (${status.digits} digits)`}
              type="password"
              inputMode="numeric"
              autoComplete="new-password"
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
            />
            <Field
              label="Type it again"
              type="password"
              inputMode="numeric"
              autoComplete="new-password"
              value={again}
              onChange={(e) => setAgain(e.target.value.replace(/\D/g, ''))}
            />
            {error && <Notice kind="error">{error}</Notice>}
            <div className="actions">
              <button type="submit" className="btn primary" disabled={saving}>
                Save the PIN
              </button>
            </div>
          </form>
        </Card>
      )}
      {!status && error && <Notice kind="error">{error}</Notice>}
    </>
  );
}
